import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, take } from 'rxjs';
import { TaasMenuItem } from 'utimaco-common-ui-angular';
import { TAAS_MENU_ITEM_LIST_TS } from '../../../taas-menuitems';
import { UserRoleService } from '../role/user-role.service';
import { Role } from '../../models/roles.enum';
import { Router, Route } from '@angular/router';
import {
  TaasEkmaasConfigurationService,
  EkmaasConfiguration,
} from '@kmaas-core/services/ekmaas/taas-ekmaas-configuration.service';

/**
 * Service to provide role-based menu items
 * This filters the main navigation menu based on the user's roles
 */
@Injectable({
  providedIn: 'root',
})
export class RoleBasedMenuService {
  private menuItemsSubject: BehaviorSubject<TaasMenuItem[]> = new BehaviorSubject<TaasMenuItem[]>([]);
  public menuItems$: Observable<TaasMenuItem[]> = this.menuItemsSubject.asObservable();

  private userRoleService = inject(UserRoleService);
  private ekmaasConfigService = inject(TaasEkmaasConfigurationService);
  private router = inject(Router);

  private ekmaasConfig: EkmaasConfiguration | undefined;

  constructor() {
    // Load organization subscription/configuration once, then gate menu by roles and config
    this.ekmaasConfigService
      .getConfigurationFromApi()
      .pipe(take(1))
      .subscribe((cfg: EkmaasConfiguration) => {
        this.ekmaasConfig = cfg;
        // Subscribe to role changes to update menu items
        this.userRoleService.userRoles$.subscribe((roles) => {
          this.updateMenuItems(roles);
        });
      });
  }

  /**
   * Update menu items based on user roles
   */
  private updateMenuItems(userRoles: Role[]): void {
    // Make a deep copy of the original menu items
    const menuItemsCopy = JSON.parse(JSON.stringify(TAAS_MENU_ITEM_LIST_TS));
    // Filter the menu items based on roles
    const filteredMenuItems = this.filterMenuItemsByRole(menuItemsCopy, userRoles);
    // Update the subject with filtered items
    this.menuItemsSubject.next(filteredMenuItems);
  }

  /**
   * get allowed menu IDs by matching routes vs menu item structure and comparing the required roles with the roles of the TaaS user
   * @returns
   */
  private getAllowedMenuIds(userRoles: Role[]): number[] {
    let resultIds: number[] = [];
    const flattenedMenuStructure = this.flattenMenuItems(TAAS_MENU_ITEM_LIST_TS);
    const menuIdPathMappingArray = flattenedMenuStructure.map((menu) => {
      return { id: menu.id, path: menu.link };
    });
    const routesFlattened = this.getFlattenedRoutesWithRolesRecursive(this.router.config);

    //check for each entry in the menu if it requires special role check and if so if user has the needed roles
    menuIdPathMappingArray.forEach((menu) => {
      const routeEntry = routesFlattened.find((entry) => entry.path === menu.path);
      if (routeEntry) {
        // Check if menu item is only to be shown depending on whether org can manage their own HSMs
        const showEntryIfPermissionsAllow =
          routeEntry.ifCanManageHsm == undefined ||
          routeEntry.ifCanManageHsm === this.ekmaasConfig?.customer_managed_hsm;

        if (showEntryIfPermissionsAllow) {
          if (routeEntry.requiredRoles && routeEntry.requiredRoles.length > 0) {
            //roles required so check if user has the needed roles
            const roleMatchFound = routeEntry.requiredRoles.some((item) =>
              Object.values(userRoles).includes(item as Role),
            );
            if (roleMatchFound) {
              resultIds.push(menu.id);
            }
          } else {
            //no roles required, so we show this entry
            resultIds.push(menu.id);
          }
        }
      }
    });

    // Apply subscription-based gating (temporary workaround until subscription service is ready)
    if (this.ekmaasConfig) {
      if (this.ekmaasConfig.max_amount_instances_azure === 0 && this.ekmaasConfig.max_amount_instances_dke === 0) {
        // Hide Cloud Clients section when both Azure and DKE are disabled
        resultIds = resultIds.filter((id) => id !== 100);
      }
      if (this.ekmaasConfig.max_amount_instances_azure === 0) {
        // Hide Azure BYOK menu item under Cloud Clients
        resultIds = resultIds.filter((id) => id !== 101);
      }
      if (this.ekmaasConfig.max_amount_instances_dke === 0) {
        // Hide Microsoft DKE menu item under Cloud Clients
        resultIds = resultIds.filter((id) => id !== 102);
      }
      if (this.ekmaasConfig.max_amount_instances_azure_hyok === 0) {
        // Hide Azure HYOK menu item under Cloud Clients
        resultIds = resultIds.filter((id) => id !== 103);
      }
      if (this.ekmaasConfig.max_amount_instances_google_hyok === 0) {
        // Hide Google HYOK menu item under Cloud Clients
        resultIds = resultIds.filter((id) => id !== 104);
      }
      if (this.ekmaasConfig.max_amount_instances_kmip === 0) {
        // Hide Enterprise Clients section
        resultIds = resultIds.filter((id) => id !== 2);
      }
      // HSM: hide HSM menu when org cannot manage HSMs;
      if (this.ekmaasConfig.customer_managed_hsm === false) {
        resultIds = resultIds.filter((id) => id !== 4 && id !== 17);
      } else {
        // Always hide legacy HSM list link
        resultIds = resultIds.filter((id) => id !== 17);
      }
    }

    // Hide TDE and REST under Enterprise Clients (temporarily not exposed in navigation)
    resultIds = resultIds.filter((id) => id !== 11 && id !== 12);

    return resultIds;
  }

  /**
   * Get a flattened array if all registered routes (with full paths for child routes) and the array of role names to allow access to this route
   * @param routes
   * @param parentPath
   * @returns
   */
  private getFlattenedRoutesWithRolesRecursive(
    routes: Route[],
    parentPath: string = '',
  ): { path: string; requiredRoles: string[]; ifCanManageHsm: boolean }[] {
    const result: { path: string; requiredRoles: string[]; ifCanManageHsm: boolean }[] = [];

    for (const route of routes) {
      // Build full path to easier match with navigation entries
      const fullPath = [parentPath, route.path].filter(Boolean).join('/');

      if (route.path != '') {
        result.push({
          path: fullPath,
          requiredRoles: route.data?.['requiredRoles'],
          ifCanManageHsm: route.data?.['ifCanManageHsm'],
        });
      }

      if (route.children && route.children.length > 0) {
        result.push(...this.getFlattenedRoutesWithRolesRecursive(route.children, fullPath));
      }
    }
    return result;
  }

  /**
   * get all menu items from the navigation tree object in a flattened array to be able to easier compare with the route config
   * @param tree
   * @returns
   */
  private flattenMenuItems(tree: TaasMenuItem[]): TaasMenuItem[] {
    const result: TaasMenuItem[] = [];

    function traverse(nodeArray: TaasMenuItem[]) {
      for (const node of nodeArray) {
        result.push(node);
        if (node.children) {
          traverse(node.children);
        }
      }
    }
    traverse(tree);
    return result;
  }

  /**
   * Filter menu items based on user roles
   */
  private filterMenuItemsByRole(menuItems: TaasMenuItem[], userRoles: Role[]): TaasMenuItem[] {
    const allowedMenuItems = this.getAllowedMenuIds(userRoles);
    const filteredItems = menuItems.filter((item) => {
      if (!allowedMenuItems.includes(item.id)) {
        return false;
      }
      if (item.type === 'section' && item.children && item.children.length > 0) {
        item.children = this.filterMenuItemsByRole(item.children, userRoles);

        // If no children left after filtering, hide the section
        if (item.children.length === 0) {
          return false;
        }
      }
      return true;
    });
    return filteredItems;
  }
}
